<template>
  <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
    <div class="row">
      <div class="col-lg-6 col-md-12 col-12">
        <Datepicker
            v-model="formData.startFrom"
            :required="true"
            :label="$t('general.start_from')"
            position="down"
            :validation="v$.startFrom"
            :messages="v$.startFrom.required.$message"/>
      </div>
      <div class="col-lg-6 col-md-12 col-12">
        <BaseNumber
            v-model="formData.price"
            :label="$t('general.price')"
            :required=true
            :min="-1000000"
            :decimalPlaces="2"
            :validation="v$.price"
            :messages="v$.price.required.$message"/>
      </div>
    </div>

    <div class="d-flex justify-content-center w-100 mt-3">
      <SaveButton
          :label="$t('general.button.save')"
          :loading="isSubmitting"/>
    </div>
  </form>
</template>

<script setup lang="ts">
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import {computed, defineEmits, defineProps, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import courseAssistedLivingCoachingService from "@/api/Course/AssistedLivingCoaching";
import Datepicker from "@/components/picker/Datepicker.vue";
import BaseNumber from "@/components/base/BaseNumber.vue";
import {requiredRule} from "@/shared/validators/validationRules";
import useVuelidate from "@vuelidate/core";
import SaveButton from "@/components/button/SaveButton.vue";

interface PriceFormData {
  startFrom: string;
  price: number | null;
}

interface Props {
  priceId: number;
  submitForm: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'update:submitForm': [value: boolean];
}>();

const {t} = useI18n();
const isLoaded = ref(false);
const isSubmitting = ref(false);

const formData = ref<PriceFormData>({
  startFrom: '',
  price: null
});

const formRules = ref({
  startFrom: requiredRule(t),
  price: requiredRule(t),
});

const computedRules = computed(() => formRules.value);
const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (props.priceId) {
      await courseAssistedLivingCoachingService.putPrice(formData.value, +props.priceId);
    } else {
      await courseAssistedLivingCoachingService.postPrice(formData.value);
    }

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getPriceData(priceId: number) {
  try {
    const {data} = await courseAssistedLivingCoachingService.getPrice(+priceId);
    formData.value = {
      startFrom: data.data.startFrom,
      price: data.data.price,
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  if (props.priceId) {
    await getPriceData(props.priceId);
  }

  isLoaded.value = true;
});
</script>
<style scoped>

</style>